/**
 * crm/_shared.jsx — Phase 75 refactor
 *
 * Shared constants + primitive components used across the CRM sub-modules.
 * Pulled out of CRMShell.jsx so each tab/component can import only what it
 * needs without re-declaring formatters or vocabulary.
 */
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

/* ---------------- Vocabulary (Phase 73 + 74 + Feb 2026) ---------------- */
// LEAD_STAGES is now editable per-workspace (see /api/crm/settings). This
// list is the seed / fallback used on the very first page load before the
// settings API call resolves. Runtime code should read stages from context
// via useLeadStages() (see below) instead of importing this directly.
export const DEFAULT_LEAD_STAGES = [
  "New Inquiry", "Discovery Call", "Follow-up",
  "Proposal Requested", "Proposal Sent", "Lost",
];
// Legacy alias kept for imports that haven't migrated yet.
export const LEAD_STAGES = DEFAULT_LEAD_STAGES;

// Stage colour palette. Keyed by CANONICAL name so a renamed stage inherits
// the tone based on its position/purpose. Anything unknown falls back to a
// neutral slate. Renamed stages get a stable colour by index (see toneFor).
const STAGE_TONES_MAP = {
  "New Inquiry":         { dot: "bg-slate-400",    header: "text-slate-700" },
  "Discovery Call":      { dot: "bg-blue-400",     header: "text-blue-700" },
  "Follow-up":           { dot: "bg-cyan-500",     header: "text-cyan-700" },
  "Strategy":            { dot: "bg-cyan-500",     header: "text-cyan-700" },  // Analogy-specific rename
  "Proposal Requested":  { dot: "bg-amber-500",    header: "text-amber-700" },
  "One Pager":           { dot: "bg-amber-500",    header: "text-amber-700" },  // Analogy-specific rename
  "Proposal Sent":       { dot: "bg-emerald-500",  header: "text-emerald-700" },
  "Lost":                { dot: "bg-rose-500",     header: "text-rose-700" },
};

// Ordered palette used to auto-assign a tone to a renamed stage by position.
const STAGE_PALETTE = [
  { dot: "bg-slate-400",   header: "text-slate-700"   },
  { dot: "bg-blue-400",    header: "text-blue-700"    },
  { dot: "bg-cyan-500",    header: "text-cyan-700"    },
  { dot: "bg-amber-500",   header: "text-amber-700"   },
  { dot: "bg-emerald-500", header: "text-emerald-700" },
  { dot: "bg-rose-500",    header: "text-rose-700"    },
  { dot: "bg-violet-500",  header: "text-violet-700"  },
  { dot: "bg-lime-500",    header: "text-lime-700"    },
];

// Kept for backwards compatibility with any code that still does
// `STAGE_TONES[stage]`. New code should call `stageToneFor(stage, index)`.
export const STAGE_TONES = new Proxy(STAGE_TONES_MAP, {
  get: (target, prop) => target[prop] || STAGE_PALETTE[0],
});

/** Feb 2026 · Resolves a colour tone for a stage label. Uses the canonical
 *  map when a known name matches; otherwise falls back to a stable palette
 *  index so custom names still get a distinctive colour. */
export function stageToneFor(stage, index = 0) {
  return STAGE_TONES_MAP[stage] || STAGE_PALETTE[index % STAGE_PALETTE.length];
}

/* ---------------- Runtime lead-stage store (Feb 2026) ---------------- */
// Simple event-emitter cache so every Kanban / drawer / drop-down that
// needs the current stage vocabulary shares one snapshot + a single
// refetch. Avoids a per-component fetch AND avoids pulling in a heavier
// context provider just for six strings.
import api from "@/lib/api";
let _stagesCache = null;
let _reservedCache = [];
const _stagesListeners = new Set();

async function _fetchStagesInternal() {
  const { data } = await api.get("/crm/pipeline/stages");
  _stagesCache = data.stages || DEFAULT_LEAD_STAGES;
  _reservedCache = data.reserved_lead_stages || [];
  _stagesListeners.forEach((cb) => cb(_stagesCache));
  return _stagesCache;
}

/** Hook — subscribe to the current lead-stage vocabulary. Returns
 *  `{stages, reserved, refresh, rename}`. Falls back to defaults until the
 *  first fetch resolves so the Kanban never renders zero columns. */
export function useLeadStages() {
  const [stages, setStages] = useState(_stagesCache || DEFAULT_LEAD_STAGES);
  const [reserved, setReserved] = useState(_reservedCache);
  useEffect(() => {
    const cb = (next) => {
      setStages(next);
      setReserved(_reservedCache);
    };
    _stagesListeners.add(cb);
    if (!_stagesCache) _fetchStagesInternal().catch(() => { /* keep defaults */ });
    return () => { _stagesListeners.delete(cb); };
  }, []);
  const refresh = () => _fetchStagesInternal();
  /** Atomically rename a stage. Backend migrates every lead sitting on the
   *  old label. Resolves with `{stages, renamed}`. */
  const rename = async (from, to) => {
    const { data } = await api.post("/crm/settings/stages/rename", { from, to });
    _stagesCache = data.stages;
    _stagesListeners.forEach((cb) => cb(_stagesCache));
    return data;
  };
  return { stages, reserved, refresh, rename };
}

// Phase 74 — BD vocabulary shared across Accounts + Prospects + Leads.
export const LEAD_TYPES = ["Cold", "Warm", "Hot"];
export const SERVICES = ["Quick Launch", "MVP", "Product Path", "Concept Design", "Launch Support", "NA", "Rendering"];
export const ICP_OPTIONS = [
  "ICP 1 - First Time Founder",
  "ICP 2 - Experienced Founder",
  "ICP 3 - Experienced Brand",
  "Student",
];
export const LEAD_STATUSES = ["Qualified", "Disqualified"];

export const LEAD_TYPE_TONE = {
  "Cold": "bg-blue-100 text-blue-700",
  "Warm": "bg-amber-100 text-amber-700",
  "Hot":  "bg-rose-100 text-rose-700",
};

// "Student" ICP must render in red per BD request.
export const ICP_TONE = (icp) => icp === "Student" ? "text-rose-600 font-medium" : "text-slate-700";

/* ---------------- Formatters ---------------- */
// ₹ formatter — always Indian rupees per Phase 74.
export const inr = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(v >= 100000000 ? 0 : 1)}Cr`;
  if (v >= 100000)   return `₹${(v / 100000).toFixed(v >= 1000000 ? 0 : 1)}L`;
  if (v >= 1000)     return `₹${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `₹${v.toLocaleString("en-IN")}`;
};

export const daysAgo = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  } catch { return null; }
};

/* ---------------- Activity timeline tone palette (Phase 74) ---------------- */
export const ACTION_TONE = {
  "crm.account.create":             { color: "text-slate-700", label: "Account created" },
  "crm.account.bulk_upload":        { color: "text-slate-700", label: "Accounts imported" },
  "crm.prospect.create":            { color: "text-blue-700",  label: "Prospect added" },
  "crm.prospect.bulk_upload":       { color: "text-blue-700",  label: "Prospects imported" },
  "crm.prospect.convert_to_lead":   { color: "text-cyan-700",  label: "Prospect → Lead" },
  "crm.lead.create":                { color: "text-cyan-700",  label: "Lead created" },
  "crm.lead.update":                { color: "text-slate-500", label: "Lead updated" },
  "crm.lead.migrate_to_deal":       { color: "text-amber-700", label: "Lead → Deal" },
  "crm.lead.public_intake":         { color: "text-emerald-700", label: "Public intake" },
  "crm.deal.create":                { color: "text-amber-700", label: "Deal created" },
  "crm.deal.update":                { color: "text-slate-500", label: "Deal updated" },
  "crm.deal.auto_create":           { color: "text-amber-700", label: "Deal auto-created" },
  "crm.deal.mark_won":              { color: "text-emerald-700", label: "Deal Won" },
  "crm.deal.mark_lost":             { color: "text-rose-700",  label: "Deal Lost" },
  "crm.client.create":              { color: "text-emerald-700", label: "Client added" },
  "crm.client.auto_create":         { color: "text-emerald-700", label: "Client auto-created" },
  "crm.client.update":              { color: "text-slate-500", label: "Client updated" },
  "crm.settings.update":            { color: "text-slate-500", label: "Settings updated" },
  "crm.intake_token.create":        { color: "text-slate-700", label: "Intake token issued" },
};

/* ---------------- Primitive components ---------------- */
export function Loading() {
  return <div className="card-flat p-6 text-sm text-slate-400 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>;
}

export function InlineText({ value, onSave, placeholder, type = "text", testId }) {
  const [draft, setDraft] = useState(String(value ?? ""));
  useEffect(() => { setDraft(String(value ?? "")); }, [value]);
  const commit = () => { if (draft !== String(value ?? "")) onSave(draft); };
  return (
    <input
      type={type}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
      placeholder={placeholder}
      className="bg-transparent border-0 w-full text-sm px-0 py-1 focus:bg-white focus:ring-1 focus:ring-[#F54800] focus:px-1.5 focus:rounded-sm transition-all outline-none"
      data-testid={testId}
    />
  );
}
